// Lightweight fetch wrapper for the Phase 5c admin app.
//
// Mirrors src/frontend/app/src/lib/apiClient.ts one-for-one — the only
// behavioral difference is the cookie that gets sent: the admin app reads
// `crauto-admin-auth` (set by /api/admin/auth/*) instead of `crauto-auth`.
// The two cookies are scoped to different subdomains in production so
// they never collide on the same browser session.

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  signal?: AbortSignal;
  /** When true the function never throws on non-2xx; caller inspects the Response. */
  raw?: boolean;
}

async function readJsonSafe(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * Issue an authenticated admin-realm request. In dev the request is
 * proxied to http://localhost:5000 (the same backend as the customer app);
 * in prod it stays same-origin with the admin SWA via Front Door.
 */
export async function adminApiFetch<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const init: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(options.body !== undefined
        ? { 'Content-Type': 'application/json' }
        : {}),
    },
    signal: options.signal,
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }

  const response = await fetch(path, init);
  if (options.raw) {
    return response as unknown as T;
  }
  if (response.status === 204) {
    return undefined as T;
  }

  const body = await readJsonSafe(response);
  if (!response.ok) {
    const message =
      typeof body === 'object' && body && 'message' in (body as Record<string, unknown>)
        ? String((body as Record<string, unknown>).message)
        : `HTTP ${response.status}`;
    throw new ApiError(response.status, message, body);
  }
  return body as T;
}
